import { Badge, Button, Card, Col, Form, Input, Row, Switch, Table, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UserAddOutlined, EditOutlined, CloseOutlined } from "@ant-design/icons";
import {
  startCrearUsuario,
  startGetUsuariosPorProveedor,
  startActualizarUsuario,
} from "../../../../../../context/actions/proveedoresActions";

export default function AgregarUsuario() {
  const { proveedorID } = useParams();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [usuarios, setUsuarios] = useState([]);
  const [editando, setEditando] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargarUsuarios = async () => {
    const data = await dispatch(startGetUsuariosPorProveedor(proveedorID));
    setUsuarios(data || []);
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const handleCrear = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const body = { ...values, proveedorID };
      delete body.password2;
      const result = await dispatch(startCrearUsuario(body));
      if (result && result.code === 0) {
        form.resetFields();
        cargarUsuarios();
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleActualizar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const body = {
        ...values,
        usuarioID: editando.usuarioID,
      };
      if (!body.password) delete body.password;
      if (!body.password2) delete body.password2;
      const result = await dispatch(startActualizarUsuario(body));
      if (result && result.code === 0) {
        setEditando(null);
        form.resetFields();
        cargarUsuarios();
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const seleccionarUsuario = (usuario) => {
    setEditando(usuario);
    form.setFieldsValue({
      nombre: usuario.nombre,
      email: usuario.email,
      password: "",
      password2: "",
      estaActivo: usuario.estaActivo,
    });
  };

  const cancelarEdicion = () => {
    setEditando(null);
    form.resetFields();
  };

  const columns = [
    { title: "Nombre", dataIndex: "nombre", key: "nombre" },
    { title: "Correo", dataIndex: "email", key: "email" },
    {
      title: "Estado",
      dataIndex: "estaActivo",
      key: "estaActivo",
      render: (val) => (
        <Tag color={val ? "green" : "red"}>
          {val ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => seleccionarUsuario(record)}
        />
      ),
    },
  ];

  return (
    <Row gutter={16} style={{ padding: 16 }}>
      <Col xs={24} md={10}>
        <Card title={editando ? "Editar Usuario" : "Crear Usuario"} size="small">
          <Form form={form} layout="vertical">
            <Form.Item
              label="Nombre"
              name="nombre"
              rules={[{ required: true, message: "El nombre es requerido" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Correo"
              name="email"
              rules={[
                { required: true, message: "El correo es requerido" },
                { type: "email", message: "Ingresa un correo válido" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Contraseña"
              name="password"
              rules={
                editando
                  ? [{ min: 6, message: "Mínimo 6 caracteres" }]
                  : [
                      { required: true, message: "La contraseña es requerida" },
                      { min: 6, message: "Mínimo 6 caracteres" },
                    ]
              }
            >
              <Input.Password placeholder={editando ? "Dejar vacío para no cambiar" : ""} />
            </Form.Item>

            <Form.Item
              label="Confirmar Contraseña"
              name="password2"
              dependencies={["password"]}
              rules={
                editando
                  ? [
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Las contraseñas no coinciden"));
                        },
                      }),
                    ]
                  : [
                      { required: true, message: "Confirma la contraseña" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Las contraseñas no coinciden"));
                        },
                      }),
                    ]
              }
            >
              <Input.Password />
            </Form.Item>

            {editando && (
              <Form.Item label="Activo" name="estaActivo" valuePropName="checked">
                <Switch />
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 0 }}>
              {editando ? (
                <Row gutter={8}>
                  <Col span={16}>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleActualizar}
                      block
                      loading={loading}
                      disabled={
                        form.getFieldsError().filter(({ errors }) => errors.length).length > 0
                      }
                    >
                      Actualizar
                    </Button>
                  </Col>
                  <Col span={8}>
                    <Button icon={<CloseOutlined />} onClick={cancelarEdicion} block>
                      Cancelar
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={handleCrear}
                      block
                      loading={loading}
                      disabled={
                        !form.isFieldsTouched(true) ||
                        form.getFieldsError().filter(({ errors }) => errors.length).length > 0
                      }
                    >
                      Crear Usuario
                    </Button>
                  )}
                </Form.Item>
              )}
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col xs={24} md={14}>
        <Card
          title="Usuarios del Proveedor"
          size="small"
          extra={<Badge count={usuarios.length} showZero />}
        >
          <Table
            dataSource={usuarios}
            columns={columns}
            rowKey="usuarioID"
            size="small"
            pagination={false}
            locale={{ emptyText: "Sin usuarios registrados" }}
          />
        </Card>
      </Col>
    </Row>
  );
}
